import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { findIana } from 'windows-iana'
import { ClinicMember } from '../../../components/clinic-member/ClinicMember'
import { SearchComponent } from '../../../components/SearchComponent/SearchComponent'
import { useClinicMembers } from './hooks/useClinicMembers'
import { getManagementSearchParams } from '../helpers/getManagementSearchParams'
import { searchParamsKeys, tabValues } from '../constants'
import { useChangeSearchText } from './hooks/useChangeSearchText'
import { useUpdateClinicMembers } from './hooks/useUpdateClinicMembers'
import { navigateToNewMemberPage } from '../helpers/navigateToNewMemberPage'
import { useChangeSearchParams } from '../../../utils/useChangeSearchParams'
import { useServiceTypes } from '../../../hooks/useServiceTypes'
import {
  Button,
  ButtonGroup,
  ChevronDownIcon,
  ChevronUpIcon,
  FilterIcon,
  LoadingSection,
  PlusIcon,
  cn,
  showToast,
} from '@quickmd-llc/design-system'
import { getSortName, getSortValue, sortOptions, states, statuses } from '../components/filter-options/utils'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import type { ServiceTypeListItemAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

// ---------------------------------------------------------------------------
// Visit type option — mirrors what generateMultiSelectOptionsList returns but
// typed explicitly so downstream callbacks can be fully typed.
// ---------------------------------------------------------------------------

type VisitTypeOption = {
  filterValue: string
  value: string
  visitTypeId: string
  isActive: boolean
}

function buildVisitTypeOptions(
  serviceTypes: ServiceTypeListItemAdminApiOutContract[],
  selectedFromParams: string | null,
): VisitTypeOption[] {
  if (!serviceTypes.length) return []
  const result: VisitTypeOption[] = []
  for (const serviceType of serviceTypes) {
    for (const visitType of serviceType.VisitTypes ?? []) {
      if (visitType.VisibilityType === 'all') {
        result.push({
          filterValue: visitType.Name?.trim() ?? '',
          value: `${serviceType.Name} (${visitType.Name?.trim()})`,
          visitTypeId: visitType.VisitTypeId ?? '',
          isActive: selectedFromParams?.includes(visitType.VisitTypeId ?? '') ?? false,
        })
      }
    }
  }
  return result
}

// ---------------------------------------------------------------------------
// Narrow the untyped adminProfile from the legacy JS reducer.
// ---------------------------------------------------------------------------

function getProfileTimeZone(profile: unknown): string | null {
  if (
    profile !== null &&
    typeof profile === 'object' &&
    'TimeZone' in profile &&
    typeof profile.TimeZone === 'string'
  ) {
    return profile.TimeZone
  }
  return null
}

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

type FilterState = { visitTypes: string; states: string }
type StateOption = { isActive: boolean; value: string; code: string }
type StatusOption = { isActive: boolean; value: string }
type PanelPos = { top: number; left: number | string; right: number | string }
type OpenFilter = 'sort' | 'licenses' | 'services' | 'status' | null

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  searchText: string
  setSearchText: (value: string) => void
  isPopstateAction: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const COUNT = 20

const ClinicMembers = ({ searchText, setSearchText, isPopstateAction }: Props) => {
  const { activeTab, searchValue } = getManagementSearchParams()
  const location = useLocation()

  const profile = useAppSelector((state) => state.accountSettings.adminProfile)
  const profileTimeZone = getProfileTimeZone(profile)

  const { serviceTypes } = useServiceTypes()
  const navigate = useNavigate()

  const [defaultTimeZone, setDefaultTimeZone] = useState<string | null>(
    profileTimeZone ? (findIana(profileTimeZone)[0] ?? null) : null,
  )
  const [isFirstRender, setIsFirstRender] = useState(true)
  const [memberType, setMemberType] = useState<'provider' | 'admin'>('provider')
  const [filter, setFilter] = useState<FilterState>({ visitTypes: '', states: '' })

  const [stateOptions, setStateOptions] = useState<StateOption[]>(states)
  const [sortValue, setSortValue] = useState('Last name (A-Z)')
  const [visitTypeOption, setVisitTypeOption] = useState<VisitTypeOption[]>([])
  const [statusOptions, setStatusOptions] = useState<StatusOption[]>(statuses)

  const adminSortOptions = sortOptions.slice(0, 2)

  const { updateClinicMembers } = useUpdateClinicMembers()
  const { onChangeSearchText } = useChangeSearchText(setSearchText)
  const { onChangeSearchParam, searchParams } = useChangeSearchParams()

  const {
    members: clinicMembers,
    isLoading,
    isError,
    errorMessage,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClinicMembers(searchParams, activeTab === tabValues.CLINIC_MEMBERS)

  // Only the true initial load (no cached data) shows the full-page loader. Background refetches
  // on tab return — isFetching with data already present — keep the cached list visible instead
  // of flashing the loader every time the Management tab is revisited.
  const loading = isLoading

  const sentinelRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const [openFilter, setOpenFilter] = useState<OpenFilter>(null)
  const [panelPos, setPanelPos] = useState<PanelPos>({ top: 0, left: 0, right: 'auto' })
  const [stateSearch, setStateSearch] = useState('')

  const toggleFilter = (key: OpenFilter, e: React.MouseEvent<HTMLButtonElement>, alignRight = false) => {
    if (openFilter === key) {
      setOpenFilter(null)
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    setPanelPos({
      top: rect.bottom + 4,
      left: alignRight ? 'auto' : rect.left,
      right: alignRight ? window.innerWidth - rect.right : 'auto',
    })
    setOpenFilter(key)
  }

  useEffect(() => {
    const tz = getProfileTimeZone(profile)
    setDefaultTimeZone(tz ? (findIana(tz)[0] ?? null) : null)
  }, [profile])

  // Load next page when sentinel scrolls into view
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasNextPage || isFetchingNextPage) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          void fetchNextPage()
        }
      },
      { threshold: 0, rootMargin: '200px' },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  useEffect(() => {
    if (activeTab === tabValues.CLINIC_MEMBERS) {
      setSearchText(searchValue)
      if (searchParams.toString() === '') {
        searchParams.set(searchParamsKeys.IS_ACTIVE, 'true')
        onChangeSearchParam({ key: searchParamsKeys.IS_DOCTOR, value: 'true' })
      }
    }
    setIsFirstRender(false)
    if (searchParams.get(searchParamsKeys.IS_ADMIN)) setMemberType('admin')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to seed search state/params from the URL; must not re-run when those params later change
  }, [])

  useEffect(() => {
    if (isPopstateAction && !isFirstRender) {
      updateClinicMembers({ count: COUNT, searchText })
      return
    }
    const trimmedLength = searchText?.trim().length ?? 0
    const hasActiveSearch = !!searchParams.get(searchParamsKeys.SEARCH_VALUE)
    // Debounce the search param. Search once there are >=3 chars, and — crucially — clear an
    // existing search when the user deletes back below the threshold (incl. to empty) so the
    // list broadens again. Previously deletions under 3 chars left the stale filter applied,
    // so only *adding* characters ever changed the results.
    if (trimmedLength >= 3 || hasActiveSearch) {
      const delayDebounceFn = setTimeout(() => {
        updateClinicMembers({
          count: COUNT,
          searchText: trimmedLength >= 3 ? searchText : '',
          isNeedToChangeSearchParam: !isPopstateAction,
        })
      }, 500)
      return () => clearTimeout(delayDebounceFn)
    }
  }, [searchText]) // eslint-disable-line react-hooks/exhaustive-deps -- debounced search intentionally keys only on searchText; other deps are stable and excluded to avoid refetch loops

  // Replaces the old ErrorAlert + 3.5 s timer — fires showToast on error.
  useEffect(() => {
    if (isError && errorMessage) {
      showToast({ type: 'error', message: errorMessage, position: 'bottom-right' })
    }
  }, [isError, errorMessage])

  // Sync visit type options when service types load
  useEffect(() => {
    setVisitTypeOption(buildVisitTypeOptions(serviceTypes, searchParams.get('visitTypes')))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync visit-type options only when serviceTypes load; searchParams is read once and intentionally excluded
  }, [serviceTypes])

  const isAdmin = memberType === 'admin'

  // Sync filter state from URL when isAdmin changes
  useEffect(() => {
    if (searchParams.get(searchParamsKeys.IS_DOCTOR)) {
      const stateParam = searchParams.get(searchParamsKeys.STATE)?.split(',') ?? []
      setStateOptions((opts) => opts.map((s) => ({ ...s, isActive: stateParam.includes(s.code) })))
      const visitParam = searchParams.get(searchParamsKeys.VISIT_TYPES)?.split(',') ?? []
      let visitNames: string[] = []
      setVisitTypeOption((opts) => {
        const newOpts = opts.map((visit) => {
          const isIncluded = visitParam.includes(visit.visitTypeId)
          if (isIncluded) visitNames = visitNames.concat(visit.filterValue)
          return { ...visit, isActive: isIncluded }
        })
        setFilter({ visitTypes: visitNames.join(';'), states: stateParam.join(';') })
        return newOpts
      })
    }
    const updatedStatus: StatusOption[] = [...statuses]
    const isActive = searchParams.get(searchParamsKeys.IS_ACTIVE)
    if (updatedStatus[0]) updatedStatus[0].isActive = isActive === 'true'
    if (updatedStatus[1]) updatedStatus[1].isActive = isActive === 'false'
    if (searchParams.toString() === '' && updatedStatus[0]) updatedStatus[0].isActive = true
    const isVerified = searchParams.get(searchParamsKeys.IS_VERIFIED)
    if (updatedStatus[2]) updatedStatus[2].isActive = isVerified === 'true'
    if (updatedStatus[3]) updatedStatus[3].isActive = isVerified === 'false'
    setStatusOptions(updatedStatus)
    const sortKey = searchParams.get(searchParamsKeys.SORT)
    setSortValue(sortKey ? getSortName(sortKey) : 'Last name (A-Z)')
    // eslint-disable-next-line react-hooks/exhaustive-deps -- resync filters from the URL only when the member type (isAdmin) changes; searchParams intentionally excluded to avoid loops
  }, [isAdmin])

  const onSetStateOptions = useCallback(
    (newStateOptions: StateOption[]) => {
      setStateOptions(newStateOptions)
      const newList = newStateOptions
        .map((s) => (s.isActive ? s.code : undefined))
        .filter((c): c is string => c !== undefined)
        .join(';')
      setFilter((prev) => ({ ...prev, states: newList }))
      onChangeSearchParam({ key: searchParamsKeys.STATE, value: newList.replaceAll(';', ',') })
    },
    [onChangeSearchParam],
  )

  const onSetVisitTypesOptions = useCallback(
    (newVisitTypeOption: VisitTypeOption[]) => {
      setVisitTypeOption(newVisitTypeOption)
      const active = newVisitTypeOption.filter((v) => v.isActive)
      setFilter((prev) => ({ ...prev, visitTypes: active.map((v) => v.filterValue).join(';') }))
      onChangeSearchParam({
        key: searchParamsKeys.VISIT_TYPES,
        value: active.map((v) => v.visitTypeId).join(),
      })
    },
    [onChangeSearchParam],
  )

  const onSetStatusValue = useCallback(
    (options: StatusOption[]) => {
      if (options[0]?.isActive && options[1]?.isActive && options[2]?.isActive && options[3]?.isActive) {
        searchParams.delete(searchParamsKeys.IS_VERIFIED)
        onChangeSearchParam({ key: searchParamsKeys.IS_ACTIVE, value: '' })
      } else {
        if ((options[0]?.isActive && options[1]?.isActive) || (!options[0]?.isActive && !options[1]?.isActive)) {
          searchParams.delete(searchParamsKeys.IS_ACTIVE)
        } else if (options[0]?.isActive) {
          searchParams.set(searchParamsKeys.IS_ACTIVE, 'true')
        } else if (options[1]?.isActive) {
          searchParams.set(searchParamsKeys.IS_ACTIVE, 'false')
        }
        if ((options[2]?.isActive && options[3]?.isActive) || (!options[2]?.isActive && !options[3]?.isActive)) {
          onChangeSearchParam({ key: searchParamsKeys.IS_VERIFIED, value: '' })
        } else if (options[2]?.isActive) {
          onChangeSearchParam({ key: searchParamsKeys.IS_VERIFIED, value: 'true' })
        } else if (options[3]?.isActive) {
          onChangeSearchParam({ key: searchParamsKeys.IS_VERIFIED, value: 'false' })
        }
      }
      setStatusOptions(options)
    },
    [onChangeSearchParam, searchParams],
  )

  const onSetSortsValue = useCallback(
    (value: string) => {
      setSortValue(value)
      onChangeSearchParam({ key: searchParamsKeys.SORT, value: getSortValue(value) })
    },
    [onChangeSearchParam],
  )

  const getVisibilityTag = useCallback((name: string | null | undefined) => {
    if (name === 'repeat') return 'Repeat Patients Only'
    if (name === 'modified') return 'Modified Booking'
    return ''
  }, [])

  useEffect(() => {
    if (!openFilter) return
    const handler = (e: MouseEvent) => {
      const inHeader = headerRef.current?.contains(e.target instanceof Node ? e.target : null)
      const target = e.target instanceof Element ? e.target : null
      const inPanel = target?.closest('.cm-filter-panel')
      if (!inHeader && !inPanel) {
        setOpenFilter(null)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [openFilter])

  return (
    <>
      {/* Header bar — segmented control (left), expanded search (center), New member (right). */}
      <div className='flex items-center justify-between gap-sp-3 rounded-t-large border border-b-0 border-shade-10 bg-white px-sp-3 pb-sp-2 pt-sp-2.5'>
        {/* DS ButtonGroup (segmented control). ButtonGroup has no style prop; outer div carries the
            pill-track.
            Track fill  → Fills/Surface-Disabled #141E1F1A = bg-shade-10
            Active pill → Cards/Fill-Active #E8F2F5 = bg-surface-active (NOT white)
            Both labels → Text SemiBold; active text highlight #178199, inactive text-light.
        */}
        <div className='flex shrink-0 rounded-full bg-shade-10 p-sp-0.5'>
          <ButtonGroup
            type='connected'
            className='w-auto !gap-0 !rounded-full !border-0 !bg-transparent !p-0'
            selectedIndex={memberType === 'provider' ? 0 : 1}
            onButtonSelection={(index: number) => {
              if (index === 0) {
                setMemberType('provider')
                searchParams.delete(searchParamsKeys.IS_ADMIN)
                onChangeSearchParam()
                onChangeSearchParam({ key: searchParamsKeys.IS_DOCTOR, value: 'true' })
              } else {
                setMemberType('admin')
                searchParams.delete(searchParamsKeys.IS_DOCTOR)
                searchParams.delete(searchParamsKeys.STATE)
                searchParams.delete(searchParamsKeys.VISIT_TYPES)
                searchParams.delete(searchParamsKeys.SORT)
                onChangeSearchParam({ key: searchParamsKeys.IS_ADMIN, value: 'true' })
              }
            }}>
            <Button
              priority='tertiary'
              className={`!m-0 !ml-0 !w-auto !cursor-pointer !rounded-full !border-none !px-sp-2.5 !py-sp-1 !font-semibold ${
                memberType === 'provider' ?
                  '!bg-surface-active !text-text-highlight !shadow-sm'
                : '!bg-transparent !text-text-light'
              }`}>
              Clinicians
            </Button>
            <Button
              priority='tertiary'
              className={`!m-0 !ml-0 !w-auto !cursor-pointer !rounded-full !border-none !px-sp-2.5 !py-sp-1 !font-semibold ${
                memberType === 'admin' ?
                  '!bg-surface-active !text-text-highlight !shadow-sm'
                : '!bg-transparent !text-text-light'
              }`}>
              Admins
            </Button>
          </ButtonGroup>
        </div>

        {/* Search + New member grouped on the right, gap-sp-3 (24px) apart. The search is the
            design-system SearchAutocomplete capped at 320px (not stretched), right-aligned. The
            group and the search carry min-w-0 so the search can shrink below 320px on narrow
            breakpoints — without it the fixed width can't shrink and the row overflows the card,
            pushing New member outside the table. */}
        <div className='flex min-w-0 items-center gap-sp-3'>
          <div className='w-[320px] min-w-0'>
            <SearchComponent
              clinic
              placeholder='Search name'
              searchText={searchText}
              onChangeSearchText={onChangeSearchText(searchText, COUNT)}
              // className lands on SearchAutocomplete's wrapper — w-full to fill the fixed-width
              // slot. All other styling is the DS default; the clear (X) button re-enables its own
              // pointer events upstream, so no override is needed here.
              additionalClassName={cn('w-full')}
            />
          </div>
          <Button
            priority='secondary'
            size='m'
            className='shrink-0'
            onClick={navigateToNewMemberPage(navigate, location)}
            id='newClinicMembersBtn'>
            <PlusIcon iconSize='m' />
            New member
          </Button>
        </div>
      </div>

      {/* Table wrapper
          SCSS: clinic-members-table-wrapper → border border-shade-10 rounded-b-large
                                               overflow-visible flex flex-col
      */}
      <div className='flex flex-1 flex-col overflow-visible rounded-b-large border border-shade-10 bg-white'>
        {/* Table header
            SCSS: clinic-members-table-header → flex items-center gap-sp-3 py-[10px] px-sp-3
                                                bg-deep-navy-5 border-b border-shade-10
                  (py-[10px] arbitrary — sp-1=8px, sp-1.5=12px; no exact 10px token)
        */}
        <div
          className='flex items-center gap-sp-3 border-b border-shade-10 bg-deep-navy-5 px-sp-3 py-[10px]'
          ref={headerRef}>
          {/* Name + Sort
              cm-col-header-btn: inline-flex items-center gap-sp-0.5 bg-transparent border-0 p-0
                                 cursor-pointer text-text-light hover:text-text-dark
                                 has-active → text-text-highlight
              cm-header-label:  text-size-2 font-semibold leading-4 whitespace-nowrap text-inherit
              cm-sort-arrows:   inline-flex flex-col items-center (svg h-[8px] w-[8px] via iconSize)
          */}
          <div className='min-w-0 flex-1'>
            <button
              type='button'
              className={cn(
                'inline-flex cursor-pointer items-center gap-sp-0.5 border-0 bg-transparent p-0 text-text-light hover:text-text-dark',
                { 'text-text-highlight': sortValue !== 'Last name (A-Z)' },
              )}
              onClick={(e) => toggleFilter('sort', e)}>
              <span className='whitespace-nowrap text-size-2 font-semibold leading-4 text-inherit'>Name</span>
              <span className='inline-flex flex-col items-center'>
                <ChevronUpIcon iconSize='xs' />
                <ChevronDownIcon iconSize='xs' />
              </span>
            </button>
          </div>

          {
            isAdmin ?
              /* Email column (admin view) — flex-1 min-w-0 matches the body Email column */
              <div className='min-w-0 flex-1'>
                <span className='whitespace-nowrap text-size-2 font-semibold leading-4 text-text-light'>Email</span>
              </div>
              /* Provider-only columns. Widths MUST match ClinicMember body columns so header and
               rows line up. Below xl they narrow to w-[96px] (label + filter icon still fit, and
               the body shows a count badge); at xl+ they expand to Licenses w-[176px] /
               Services w-[300px] with full chips. Utilization is hidden until we have real
               utilization data (body only had a `--` placeholder). */
            : <>
                <div className='w-[96px] shrink-0 xl:w-[176px] xl:min-w-[100px] xl:shrink'>
                  <button
                    type='button'
                    className={cn(
                      'inline-flex cursor-pointer items-center gap-sp-0.5 border-0 bg-transparent p-0 text-text-light hover:text-text-dark',
                      { 'text-text-highlight': stateOptions.some((s) => s.isActive) },
                    )}
                    onClick={(e) => {
                      toggleFilter('licenses', e)
                      setStateSearch('')
                    }}>
                    <span className='whitespace-nowrap text-size-2 font-semibold leading-4 text-inherit'>Licenses</span>
                    <FilterIcon iconSize='xs' />
                  </button>
                </div>
                <div className='w-[96px] shrink-0 xl:w-[300px] xl:min-w-[160px] xl:shrink'>
                  <button
                    type='button'
                    className={cn(
                      'inline-flex cursor-pointer items-center gap-sp-0.5 border-0 bg-transparent p-0 text-text-light hover:text-text-dark',
                      { 'text-text-highlight': visitTypeOption.some((v) => v.isActive) },
                    )}
                    onClick={(e) => toggleFilter('services', e)}>
                    <span className='whitespace-nowrap text-size-2 font-semibold leading-4 text-inherit'>Services</span>
                    <FilterIcon iconSize='xs' />
                  </button>
                </div>
              </>

          }

          {/* Status filter — w-[96px] matches body Status column */}
          <div className='w-[96px] shrink-0'>
            <button
              type='button'
              className={cn(
                'inline-flex cursor-pointer items-center gap-sp-0.5 border-0 bg-transparent p-0 text-text-light hover:text-text-dark',
                {
                  'text-text-highlight': statusOptions.slice(1).some((s) => s.isActive) || !statusOptions[0]?.isActive,
                },
              )}
              onClick={(e) => toggleFilter('status', e, true)}>
              <span className='whitespace-nowrap text-size-2 font-semibold leading-4 text-inherit'>Status</span>
              <FilterIcon iconSize='xs' />
            </button>
          </div>

          {/* Arrow column spacer — w-sp-3 matches body chevron column */}
          <div className='w-sp-3 shrink-0' />
        </div>

        {/* Filter panels — rendered via position:fixed (set inline) to escape overflow containers.
            cm-filter-panel: z-max bg-white border border-shade-10 rounded-large
                             shadow-[0_4px_16px_rgba(37,56,69,0.12)] min-w-[180px] py-sp-0.5
                             (shadow arbitrary — DS hover-large uses rgba(20,30,31,0.10) and 4/8px,
                              this panel uses a darker tint and larger spread)
            cm-filter-panel--scroll adds: max-h-[260px] overflow-y-auto
        */}
        {openFilter === 'sort' && (
          <div
            className='cm-filter-panel z-max min-w-[180px] rounded-large border border-shade-10 bg-white py-sp-0.5 shadow-[0_4px_16px_rgba(37,56,69,0.12)]'
            style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, right: panelPos.right }}>
            {(isAdmin ? adminSortOptions : sortOptions).map((opt) => (
              /* cm-filter-sort-option: block w-full py-sp-1 px-sp-2 bg-transparent border-0
                 text-left cursor-pointer text-size-3 text-text-dark whitespace-nowrap
                 hover:bg-deep-navy-5; is-selected → font-semibold text-text-highlight
              */
              <button
                key={opt}
                type='button'
                className={cn(
                  'block w-full cursor-pointer whitespace-nowrap border-0 bg-transparent px-sp-2 py-sp-1 text-left text-size-3 font-normal text-text-dark hover:bg-deep-navy-5',
                  { 'font-semibold text-text-highlight': sortValue === opt },
                )}
                onClick={() => {
                  onSetSortsValue(opt)
                  setOpenFilter(null)
                }}>
                {opt}
              </button>
            ))}
          </div>
        )}
        {openFilter === 'licenses' && (
          <div
            className='cm-filter-panel z-max max-h-[260px] min-w-[180px] overflow-y-auto rounded-large border border-shade-10 bg-white py-sp-0.5 shadow-[0_4px_16px_rgba(37,56,69,0.12)]'
            style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, right: panelPos.right }}>
            {/* cm-filter-search-wrap: pt-sp-1 px-sp-1.5 pb-sp-0.5 */}
            <div className='px-sp-1.5 pb-sp-0.5 pt-sp-1'>
              {/* cm-filter-search: w-full h-[32px] px-[10px] border border-shade-10
                  rounded-[6px] text-size-3 text-text-dark outline-none box-border
                  placeholder:text-text-light focus:border-text-highlight
                  (rounded-[6px] arbitrary — rounded-large=8px, rounded-medium=4px; 6px has no DS token)
              */}
              <input
                className='box-border h-[32px] w-full rounded-[6px] border border-shade-10 px-[10px] text-size-3 text-text-dark outline-none placeholder:text-text-light focus:border-text-highlight'
                placeholder='Search'
                value={stateSearch}
                onChange={(e) => setStateSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
            {stateOptions
              .filter((s) => s.value.toLowerCase().includes(stateSearch.toLowerCase()))
              .map((opt) => (
                /* cm-filter-option: flex items-center gap-sp-1 py-sp-1 px-sp-2
                   text-size-3 font-normal text-text-dark whitespace-nowrap cursor-pointer
                   hover:bg-deep-navy-5
                   checkbox accent-[#178199]: accent-color only accepts hex, not Tailwind class;
                   #178199 = coastalBlue[50] = text-text-highlight hex value
                */
                <label
                  key={opt.code}
                  className='flex cursor-pointer items-center gap-sp-1 whitespace-nowrap px-sp-2 py-sp-1 text-size-3 font-normal text-text-dark hover:bg-deep-navy-5'>
                  <input
                    type='checkbox'
                    className='h-[14px] w-[14px] shrink-0 cursor-pointer accent-[#178199]'
                    checked={opt.isActive}
                    onChange={() =>
                      onSetStateOptions(
                        stateOptions.map((s) => (s.code === opt.code ? { ...s, isActive: !s.isActive } : s)),
                      )
                    }
                  />
                  {opt.value}
                </label>
              ))}
          </div>
        )}
        {openFilter === 'services' && (
          <div
            className='cm-filter-panel z-max max-h-[260px] min-w-[180px] overflow-y-auto rounded-large border border-shade-10 bg-white py-sp-0.5 shadow-[0_4px_16px_rgba(37,56,69,0.12)]'
            style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, right: panelPos.right }}>
            {visitTypeOption.length === 0 ?
              /* cm-filter-empty: py-sp-1.5 px-sp-2 text-size-3 text-text-light */
              <div className='px-sp-2 py-sp-1.5 text-size-3 text-text-light'>No services available</div>
            : visitTypeOption.map((opt) => (
                <label
                  key={opt.visitTypeId}
                  className='flex cursor-pointer items-center gap-sp-1 whitespace-nowrap px-sp-2 py-sp-1 text-size-3 font-normal text-text-dark hover:bg-deep-navy-5'>
                  <input
                    type='checkbox'
                    className='h-[14px] w-[14px] shrink-0 cursor-pointer accent-[#178199]'
                    checked={opt.isActive}
                    onChange={() =>
                      onSetVisitTypesOptions(
                        visitTypeOption.map((v) =>
                          v.visitTypeId === opt.visitTypeId ? { ...v, isActive: !v.isActive } : v,
                        ),
                      )
                    }
                  />
                  {opt.value}
                </label>
              ))
            }
          </div>
        )}
        {openFilter === 'status' && (
          <div
            className='cm-filter-panel z-max min-w-[180px] rounded-large border border-shade-10 bg-white py-sp-0.5 shadow-[0_4px_16px_rgba(37,56,69,0.12)]'
            style={{ position: 'fixed', top: panelPos.top, left: panelPos.left, right: panelPos.right }}>
            {statusOptions.map((opt) => (
              <label
                key={opt.value}
                className='flex cursor-pointer items-center gap-sp-1 whitespace-nowrap px-sp-2 py-sp-1 text-size-3 font-normal text-text-dark hover:bg-deep-navy-5'>
                <input
                  type='checkbox'
                  className='h-[14px] w-[14px] shrink-0 cursor-pointer accent-[#178199]'
                  checked={opt.isActive}
                  onChange={() =>
                    onSetStatusValue(
                      statusOptions.map((s) => (s.value === opt.value ? { ...s, isActive: !s.isActive } : s)),
                    )
                  }
                />
                {opt.value}
              </label>
            ))}
          </div>
        )}

        {/* Rows — clinic-members-table: min-h-[80px]. The loader lives here (below the table
            header) rather than replacing the whole page, so the header + search + segmented
            control stay mounted while data loads — this also stops the collapsible search from
            resetting/collapsing on every new-search refetch. */}
        <div className='flex min-h-[80px] flex-1 flex-col'>
          {loading ?
            <div className='flex w-full flex-1'>
              <LoadingSection />
            </div>
          : clinicMembers.length > 0 ?
            clinicMembers.map((t) =>
              memberType === t.UserType ?
                <ClinicMember
                  key={t.UserId}
                  firstName={t.FirstName}
                  id={t.UserId}
                  lastName={t.LastName}
                  prefix={t.Prefix}
                  suffix={t.Suffix}
                  email={t.Email}
                  role={t.UserType}
                  photo={t.ImageUrl}
                  visitTypes={t.VisitTypes}
                  pending={!t.IsVerified}
                  isAccountActive={t.IsActive}
                  defaultTimeZone={defaultTimeZone}
                  filterOptions={filter}
                  visitTypeVisibilityOverrideStatus={getVisibilityTag(t.VisitTypeVisibilityOverrideStatus)}
                />
              : null,
            )
            /* clinic-members-empty-table-result:
               text-size-3 font-medium text-text-light py-sp-5 px-sp-3 text-center rounded-b-large
            */
          : <div className='rounded-b-large px-sp-3 py-sp-5 text-center text-size-3 font-medium text-text-light'>
              No clinic members found.
            </div>
          }
          {/* Sentinel: triggers next-page fetch when it scrolls into view */}
          <div ref={sentinelRef} />
        </div>
      </div>
    </>
  )
}

export default ClinicMembers
